/**
 * B-15 — deterministic horizontal-scroll reset at known state boundaries.
 *
 * Field evidence (beta, TestFlight Build 2, iPhone 15 Pro / 393pt / es-iOS):
 * the Today page rendered hard-shifted left (~30% clipped) IMMEDIATELY after
 * completing onboarding — the one flow that marches the user through several
 * text inputs (Step2 location, Step4 body metrics, Step5 pace, the guardian
 * invite beat) with the iOS keyboard up. WKWebView reveals a focused input by
 * panning the viewport — a NATIVE scroll the B-2 CSS lock (`overflow-x: clip`)
 * does not govern — and is known to leave that pan un-restored after the
 * keyboard dismisses. Onboarding → Today is a same-document SPA navigation
 * (`<Navigate to="/today">`), and the app had NO scroll reset anywhere, so the
 * stale pan survived the route change; an app restart (fresh WKWebView) healed
 * it, and onboarding never runs again — which is exactly the reporter's
 * "cannot re-reproduce".
 *
 * This is NOT a watchdog: it runs once at each side of one known boundary
 * (onboarding unmount, Today route entry) where any horizontal offset is
 * provably stale — the app has no legitimate document-level horizontal scroll
 * (B-2 contract).
 *
 * Scope honesty: this clears layout-viewport/document scroll (the common
 * keyboard-reveal pan). A stale iOS auto-ZOOM (focusing a sub-16px input
 * zooms the page; scale cannot be reset from JS) is prevented at the source
 * instead — the onboarding inputs are pinned to 16px font-size
 * (onboardingInputZoom.b15.test.ts).
 */
export function resetHorizontalScroll(): void {
  if (typeof document === 'undefined') return;
  const scroller = document.scrollingElement ?? document.documentElement;
  if (scroller && scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
  // Some WebKit states scroll body instead of the documentElement.
  if (document.body && document.body.scrollLeft !== 0) document.body.scrollLeft = 0;
  // Belt: window-level scroll (keeps the current vertical position — Today
  // owns its own vertical scroll; only the horizontal axis is ever stale).
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    try {
      window.scrollTo({ left: 0, top: window.scrollY ?? 0 });
    } catch {
      // Older WebKit without options-object support.
      window.scrollTo(0, window.scrollY ?? 0);
    }
  }
}
